import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  addEntitySourcePrefix,
  createEntitySource,
  getApiErrorMessage,
  getEntitySource,
  removeEntitySourcePrefix,
  updateEntitySource,
  updateEntitySourcePrefix,
} from '../../api/client';
import type { EntitySourceDetail } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function EntitySourceFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [source, setSource] = useState<EntitySourceDetail | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isSystem, setIsSystem] = useState(false);

  const [newPrefix, setNewPrefix] = useState('');
  const [newPrefixDesc, setNewPrefixDesc] = useState('');

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit =
    user && (isNew ? canWritePlatformConfig(user) : canEditPlatformConfig(user));

  useEffect(() => {
    if (isNew || !id) return;
    getEntitySource(id)
      .then((s) => {
        setSource(s);
        setCode(s.code);
        setName(s.name);
        setDescription(s.description ?? '');
        setSortOrder(s.sortOrder);
        setIsActive(s.isActive);
        setIsSystem(s.isSystem);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const created = await createEntitySource({
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          sortOrder,
          isActive,
        });
        navigate(PLATFORM_CONFIG.source(created.id));
      } else if (id) {
        const updated = await updateEntitySource(id, {
          name: name.trim(),
          description: description.trim() || null,
          sortOrder,
          isActive,
        });
        setSource((prev) => (prev ? { ...prev, ...updated } : prev));
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddPrefix() {
    if (!id || id === 'new' || !newPrefix.trim()) return;
    setError('');
    try {
      const updated = await addEntitySourcePrefix(id, {
        prefix: newPrefix.trim().toUpperCase(),
        description: newPrefixDesc.trim() || undefined,
      });
      setSource(updated);
      setNewPrefix('');
      setNewPrefixDesc('');
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  async function handleTogglePrefix(prefixId: string, active: boolean) {
    if (!id) return;
    setError('');
    try {
      await updateEntitySourcePrefix(prefixId, { isActive: active });
      const updated = await getEntitySource(id);
      setSource(updated);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  async function handleRemovePrefix(prefixId: string, prefix: string) {
    if (!window.confirm(`Remove prefix "${prefix}"?`)) return;
    setError('');
    try {
      const updated = await removeEntitySourcePrefix(prefixId);
      setSource(updated);
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  if (!canEdit) return <div className="alert alert-error">No permission.</div>;
  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <Link to={PLATFORM_CONFIG.sources} style={{ fontSize: '0.85rem' }}>
          ← Sources
        </Link>
        <h1>{isNew ? 'New source' : `Source — ${code}`}</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <form className="card card-body" style={{ maxWidth: 640 }} onSubmit={handleSave}>
        <div className="form-group">
          <label>Code</label>
          <input
            className="form-control"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            disabled={!isNew}
            placeholder="ERP"
          />
          {!isNew && isSystem && (
            <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.35rem' }}>
              System source — code cannot be changed.
            </p>
          )}
        </div>
        <div className="form-group">
          <label>Name</label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            className="form-control"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Sort order</label>
          <input
            type="number"
            min={0}
            className="form-control"
            style={{ maxWidth: 120 }}
            value={sortOrder}
            onChange={(e) => setSortOrder(Number(e.target.value))}
          />
        </div>
        <label>
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />{' '}
          Active
        </label>
        <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>

      {!isNew && source && (
        <div className="card card-body" style={{ maxWidth: 640, marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginTop: 0 }}>Enrollment prefix rules</h2>
          <p className="text-muted" style={{ fontSize: '0.85rem' }}>
            Prefixes help agents recognize enrollment numbers for this source. Longest match wins
            when showing hints on the create-ticket form.
          </p>

          {source.enrollmentPrefixes.length === 0 ? (
            <p className="text-muted">No prefix rules yet.</p>
          ) : (
            <table className="data-table" style={{ marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th>Prefix</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {source.enrollmentPrefixes.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <code>{p.prefix}</code>
                    </td>
                    <td>{p.description ?? '—'}</td>
                    <td>
                      <Badge
                        label={p.isActive ? 'ACTIVE' : 'INACTIVE'}
                        className={p.isActive ? 'badge-success' : 'badge-neutral'}
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => void handleTogglePrefix(p.id, !p.isActive)}
                      >
                        {p.isActive ? 'Deactivate' : 'Activate'}
                      </button>{' '}
                      <button
                        type="button"
                        className="btn btn-danger-ghost"
                        onClick={() => void handleRemovePrefix(p.id, p.prefix)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="search-row">
            <input
              className="form-control"
              placeholder="Prefix e.g. 21KE"
              value={newPrefix}
              onChange={(e) => setNewPrefix(e.target.value.toUpperCase())}
            />
            <input
              className="form-control"
              placeholder="Description (optional)"
              value={newPrefixDesc}
              onChange={(e) => setNewPrefixDesc(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleAddPrefix()}
              disabled={!newPrefix.trim()}
            >
              Add prefix
            </button>
          </div>
        </div>
      )}
    </>
  );
}
